import { Button } from "@mui/material";
import { useLogs } from "api/logs";
import { useAppSelector } from "app/hooks";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { DateTimeInput } from "components/FormControls/DateTimeInput";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderMenuButton } from "components/Header/Subheader/SubheaderMenuButton";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { createHeadItems, OLTable } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { OLModal } from "components/Tooltips/OLModal";
import { Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import _ from "lodash";
import { useEffect, useState } from "react";
import { Form } from "react-router-dom";
import { selectTables } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { LOGS_LIST_ID } from "state/tables/types";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { ListingActions, ListResponse, Order } from "types/listing";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import { useStyles } from "./Settings.styles";

export enum LogModals {
  Archives,
  Details,
}

const createItems = (items: any) => {
  return items.map((item: any) => ({
    cells: [
      { content: item.auditLogId, property: "auditLogId" },
      {
        align: "right",
        content: OLDate(item.createdAt),
        property: "createdAt",
      },
      { align: "right", content: item.store?.name, property: "store" },
      { align: "right", content: item.userId },
      { align: "right", content: item.username },
      { align: "right", content: item.ip },
      { align: "right", content: item.userType },
      { align: "right", content: item.eventType },
      { align: "right", content: item.entityType },
      { align: "right", content: item.entityId },
    ],
    id: item.auditLogId,
  }));
};

export function SystemLogsList() {
  const classes = useStyles();
  const { message } = useMessage();
  const { exportLogs, useLogsList } = useLogs();

  const [selectedLogModal, setSelectedLogModal] = useState<
    | {
        selectedLog: { [key: string]: string };
        mode: number;
      }
    | undefined
  >();
  const [logModalOpen, setLogModalOpen] = useState(false);

  const { listing, updateListing } = useListing(LOGS_LIST_ID);
  const tables = useAppSelector(selectTables);
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();
  const [filters, setFilters] = useState<SelectedFilter[]>(
    tables[LOGS_LIST_ID]?.filters || [],
  );
  const { data: logsList, error } = useLogsList({
    filters: parseFiltersForBE(getNewFiltersOnly(filters)),
    order: listing.order || "desc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "createdAt",
  }) as { data: ListResponse; error?: any };

  const setSelectedFilters = (filters: SelectedFilterV2[]) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      LOGS_LIST_ID,
    );

    setFilters(filters);
  };
  const headItems = createHeadItems([
    {
      headerText: message({ id: "logID" }),
      property: "auditLogId",
      hasMinWidthThreshold: true,
      isSortable: true,
    },
    {
      align: "right",
      headerText: message({ id: "createdOn" }),
      property: "createdAt",
      hasMinWidthThreshold: true,
      isSortable: true,
    },
    {
      align: "right",
      headerText: message({ id: "tenant" }),
      property: "store",
      hasMinWidthThreshold: true,
      isSortable: true,
    },
    {
      align: "right",
      headerText: message({ id: "userID" }),
      sortable: false,
      hasMinWidthThreshold: true,

      isSortable: false,
    },
    {
      align: "right",
      headerText: message({ id: "username" }),
      property: "username",
      isSortable: true,
    },
    {
      align: "right",
      headerText: message({ id: "IP" }),
      property: "ip",
      isSortable: true,
    },
    {
      align: "right",
      headerText: message({ id: "userType" }),
      property: "userType",
      isSortable: true,
    },
    {
      align: "right",
      headerText: message({ id: "eventType" }),
      property: "eventType",
      isSortable: true,
    },
    {
      align: "right",
      headerText: message({ id: "entityType" }),
      property: "entityType",
      isSortable: true,
    },
    {
      align: "right",
      headerText: message({ id: "entityID" }),
      property: "entityId",
      hasMinWidthThreshold: true,
      isSortable: true,
    },
  ]);
  const items = logsList?.items ? createItems(logsList.items) : [];

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && logsList) {
      setPersistentFilters(filters, LOGS_LIST_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, logsList, error]);

  return (
    <>
      <TopHeader
        title={message({ id: "systemLogs" })}
        clearLink={links.settings}
        breadcrumbs={{
          parent: {
            label: message({ id: "admins" }),
            path: links.settings,
            state: { activeTab: "admins" },
          },
        }}
      />
      <Subheader>
        <FiltersV2
          donutLabel={message({ id: "logs" })}
          properties={[
            {
              query: "createdAt",
              label: message({ id: "createdOn" }),
              type: "datetime",
            },
            {
              query: "entityId",
              label: message({ id: "entityID" }),
              type: "text",
            },
            {
              query: "entityType",
              label: message({ id: "entityType" }),
              type: "text",
            },
            {
              query: "eventType",
              label: message({ id: "eventType" }),
              type: "text",
            },
            {
              query: "ip",
              label: message({ id: "IP" }),
              type: "text",
            },
            {
              query: "auditLogId",
              label: message({ id: "logID" }),
              type: "text",
            },
            {
              query: "store",
              label: message({ id: "tenant" }),
              type: "tenant-name",
            },
            {
              query: "username",
              label: message({ id: "username" }),
              type: "text",
            },
            {
              query: "userType",
              label: message({ id: "userType" }),
              type: "text",
            },
          ]}
          setFilters={setSelectedFilters}
          savedFilters={getNewFiltersOnly(filters)}
          total={logsList?.total}
          data-test-id="system-logs"
        />
        <SubheaderMenuButton
          text={message({ id: "exportToCSV" })}
          menuItems={[
            {
              text: message({ id: "chooseDateRange" }),
              onClick: () => {
                setSelectedLogModal({
                  selectedLog: {},
                  mode: LogModals.Archives,
                });
                setLogModalOpen(true);
              },
            },
            {
              text: message({ id: "exportAllData" }),
              onClick: () => exportLogs(),
            },
          ]}
        />
      </Subheader>
      <OLTable
        fullView
        setColumnOrdering={setColumnOrdering}
        tableName={LOGS_LIST_ID}
        data-test-id="system-logs-list"
        actions={{
          body: [
            {
              action: (id) => {
                setSelectedLogModal({
                  selectedLog:
                    logsList?.items?.find(
                      (element: { auditLogId: string; key: string }) =>
                        element?.auditLogId === (id as string),
                    )?.data || {},
                  mode: LogModals.Details,
                });
                setLogModalOpen(true);
              },
              label: message({ id: "seeDetails" }),
            },
          ],
        }}
        columns={logsList && headItems}
        rows={logsList && items}
        onSort={(prop) =>
          updateListing({
            payload: {
              ...prop,
              onSortCustomAction: (
                order: Order,
                property: string,
                rowsPerPage: number,
                page: number,
              ) => {
                setPersistentListingSorting(
                  order,
                  property,
                  rowsPerPage,
                  page,
                  LOGS_LIST_ID,
                );
              },
            },
            type: ListingActions.SET_SORT,
          })
        }
        pagination={{
          count: calculateCount(logsList?.total.filtered, logsList?.total.all),
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
          onChangePage: (page) => {
            updateListing({ payload: page, type: ListingActions.CHANGE_PAGE });
          },
          onChangeRowsPerPage: (rows) => {
            updateListing({
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
              payload: rows,
            });
          },
        }}
        sort={{
          order: listing.order || "desc",
          property: listing.property || "createdAt",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
      />
      {selectedLogModal?.mode === LogModals.Archives ? (
        <OLModal
          header={message({ id: "exportingToCSV" })}
          onClose={() => setLogModalOpen(false)}
          open={logModalOpen}
          subHeader={message({ id: "exportingToCSVInfo" })}
        >
          <>
            <Formik
              initialValues={{ createdAtFrom: null, createdAtTo: null }}
              onSubmit={async (values, { setSubmitting, setStatus }) => {
                setSubmitting(true);
                setStatus({});
                exportLogs(values.createdAtFrom, values.createdAtTo);
              }}
            >
              {({ handleSubmit, isSubmitting, status }) => {
                return (
                  <Form onSubmit={handleSubmit}>
                    {status?.[""] && (
                      <span className={classes.error}>{status[""]}</span>
                    )}
                    <DateTimeInput
                      className={classes.modalInput}
                      format="yyyy-MM-dd'T'HH:mm:ss"
                      label={`${message({ id: "dateFrom" })}`}
                      name="createdAtFrom"
                    />
                    <DateTimeInput
                      className={classes.modalInput}
                      format="yyyy-MM-dd'T'HH:mm:ss"
                      label={`${message({ id: "dateTo" })}`}
                      name="createdAtTo"
                    />
                    <Button
                      className={classes.modalButton}
                      color="primary"
                      disabled={isSubmitting}
                      fullWidth
                      type="submit"
                      variant="contained"
                    >
                      {message({ id: "export" })}
                    </Button>
                  </Form>
                );
              }}
            </Formik>
          </>
        </OLModal>
      ) : selectedLogModal?.mode === LogModals.Details ? (
        <OLModal
          header={message({ id: "logDetails" })}
          modalWidth={753}
          onClose={() => setLogModalOpen(false)}
          open={logModalOpen}
        >
          <OLTable
            setColumnOrdering={setColumnOrdering}
            disableColumnManager
            tableName={LOGS_LIST_ID}
            className={classes.oltable}
            noDataText={message({ id: "noDetailsForChosenRecord" })}
            onSort={() => {}}
            columns={createHeadItems([
              {
                property: "key",
                isSortable: false,
              },
              {
                property: "value",
                isSortable: false,
              },
            ])}
            rows={Object.keys(selectedLogModal.selectedLog)?.map(
              (key: any) => ({
                id: key,
                cells: [
                  { content: key, property: "key" },
                  {
                    content:
                      _.isArray(selectedLogModal?.selectedLog?.[key]) ||
                      _.isObject(selectedLogModal?.selectedLog?.[key])
                        ? JSON.stringify(selectedLogModal?.selectedLog?.[key])
                        : selectedLogModal?.selectedLog?.[key],
                    property: "value",
                  },
                ],
              }),
            )}
            sort={{ order: "asc", property: "", rowsPerPage: 10, page: 1 }}
          />
        </OLModal>
      ) : null}
    </>
  );
}
